import type { FormEventHandler, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFetcher, useLocation, useNavigate, useOutletContext, useRouteLoaderData } from '@remix-run/react';

import ManageAction from '~/components/Actions/ManageAction';
import Button from '~/components/Button';
import StatusInputs from '~/components/Forms/StatusInputs';
import useGlobalModals from '~/components/GlobalModals/hooks/useGlobalModals';
import SlideUpModal from '~/components/Modal/SlideUpModal';
import useToast from '~/hooks/useToast';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { CommentType } from '~/interfaces/comment';
import type { OutletContext } from '~/interfaces/outlet_context';
import type { RootLoaderData } from '~/root';
import type { PostLoaderData } from '~/routes/p/$slug';

type Props = {
  isNotLoggedIn: boolean;
  onCommentCreated?: (newComment: CommentType, parentCommentId?: string) => void;
  isPremiumCommentsOnly?: boolean;
  children: (
    isSubmitting: boolean,
    setCurrentComment: (comment: CommentType['content']) => void,
    replyingToCommentId: string | null,
    setReplyingToCommentId: (value: string | null) => void,
  ) => ReactNode;
};

const CommentAction = ({ children, onCommentCreated, isNotLoggedIn, isPremiumCommentsOnly = false }: Props) => {
  const { subscription } = useRouteLoaderData('root') as RootLoaderData;
  const { post, site } = useTypedLoaderData<PostLoaderData>();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { admin } = useOutletContext<OutletContext>();
  const { handleOpenModal } = useGlobalModals();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formDataInState, setFormDataInState] = useState<FormData | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [currentComment, setCurrentComment] = useState<CommentType['content']>('');
  const [replyingToCommentId, setReplyingToCommentId] = useState<string | null>(null);

  const fetcher = useFetcher<{ toast: any }>();
  const isSubmitting = fetcher.state === 'submitting';
  const fetcherData = fetcher.data;
  const error = fetcherData?.toast?.status === 'error';
  useToast(fetcherData?.toast);

  const isAdmin = Boolean(admin);

  const shouldRenderPremiumPopup = subscription?.tier !== 'premium' && isPremiumCommentsOnly && !isAdmin;
  const needsToSetProfileDetails = !subscription?.comment_details_set && !isAdmin;

  useEffect(() => {
    if (fetcherData && !error && onCommentCreated) {
      const formData = formRef.current ? new FormData(formRef.current) : null;
      const parentCommentId = formData?.get('comment_id');
      if (typeof parentCommentId === 'string') {
        onCommentCreated(fetcherData as CommentType, parentCommentId);
      } else {
        onCommentCreated(fetcherData as CommentType);
      }
      formRef.current?.reset();
      setCurrentComment('');
      setReplyingToCommentId(null);
    }
  }, [fetcherData, error]);

  const formAction = isAdmin ? '/admins/comments' : '/comments';

  const handleSubmit: FormEventHandler = (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.currentTarget);

    if (isNotLoggedIn) {
      e.preventDefault();
      handleOpenModal('login', { preventScrollReset: true });
      return false;
    }

    if (needsToSetProfileDetails || shouldRenderPremiumPopup) {
      e.preventDefault();
      setIsModalOpen(true);
      setFormDataInState(formData);
      return false;
    }

    return true;
  };

  const handleUpdateProfileSuccess = async () => {
    if (formDataInState) {
      fetcher.submit(formDataInState, {
        method: 'POST',
        action: formAction,
      });
    }

    setFormDataInState(null);
    setIsModalOpen(false);
  };

  const handleCloseModal = () => {
    setFormDataInState(null);
    setIsModalOpen(false);
  };

  return (
    <>
      {/* Update Profile Details Prompt */}
      <SlideUpModal
        title={t('comments.commenting.profile_title') || ''}
        description={
          <div>
            <p>{t('comments.commenting.profile_description')}</p>
          </div>
        }
        // Prompt user for their profile details if this is the first time they are commenting and don't have a a profile set.
        // Without this information we would have nothing to display on screen as we obviously should never be displaying user email information publicly.
        isOpen={needsToSetProfileDetails && isModalOpen}
        onClose={handleCloseModal}
        backgroundColor={site.primary_theme.background_color}
        textColor={site.primary_theme.text_on_background_color}
        closeButtonBackgroundColor={site.primary_theme.background_color}
        closeButtonTextColor={site.primary_theme.text_on_background_color}
      >
        <ManageAction
          firstName={subscription?.first_name}
          lastName={subscription?.last_name}
          avatarUrl={subscription?.avatar_url}
          onSuccess={handleUpdateProfileSuccess}
        />
      </SlideUpModal>
      {/* Upgrade to Premium Prompt */}
      <SlideUpModal
        title={`${t('comments.upgrade.title')}`}
        description={
          <div>
            <p>{t('comments.upgrade.description')}</p>
          </div>
        }
        // Prompt a user to upgrade to premium if they are just a free user and the comments are premium only
        isOpen={shouldRenderPremiumPopup && isModalOpen}
        onClose={handleCloseModal}
        backgroundColor={site.primary_theme.background_color}
        textColor={site.primary_theme.text_on_background_color}
        closeButtonBackgroundColor={site.primary_theme.background_color}
        closeButtonTextColor={site.primary_theme.text_on_background_color}
      >
        <Button
          type="button"
          variant="primary"
          className="flex w-full justify-center"
          isLoading={isSubmitting}
          onClick={() => navigate(`/upgrade?post_url=${location.pathname}`)}
          style={{
            color: site.primary_theme.text_on_primary_color,
            backgroundColor: site.primary_theme.primary_color,
          }}
        >
          {t('comments.upgrade.cta')}
        </Button>
      </SlideUpModal>
      {/* TODO: use our ActionForm */}
      <fetcher.Form ref={formRef} method="post" action={formAction} className="contents" onSubmit={handleSubmit}>
        {post && <input type="hidden" name="post_id" value={post.id} />}
        {subscription?.id && <input type="hidden" name="subscription_id" value={subscription.id} />}
        {admin?.id && <input type="hidden" name="user_id" value={admin.id} />}
        <input value={currentComment} name="content" type="hidden" />
        <StatusInputs name="characters" errorMessage={t('comments.commenting.characters_error')} successMessage="" />
        <StatusInputs name="comment" errorMessage={t('errors.default')} successMessage="" />
        {children(isSubmitting, setCurrentComment, replyingToCommentId, setReplyingToCommentId)}
      </fetcher.Form>
    </>
  );
};

export default CommentAction;
